/**
 * 상세 머리의 「N위」 — 「전체 브랜드」 목록의 `#` 과 같은 수를 짓는다.
 *
 * 🔑 목록과 같은 훑기(`brandHubRankScan`)에서 같은 판정(`splitFlaggedRanking`)으로 빼고 다시 매긴다 — 두 수가 어긋나면 안 된다.
 * ⚠️ 순수 함수다 — 로그인한 상세(훅)와 공유 페이지(서버 출처 → 화면 모델)가 함께 쓴다.
 */

import type { BrandQualityFlag } from 'src/utils/brand/brand-quality-doc';

import { fNumber } from 'src/utils/format/format-number';
import { splitFlaggedRanking } from 'src/utils/brand/brand-quality-doc';

import { BRAND_HUB_RANK_SCAN } from '../scope/brand-hub-overview-scope';

/** 몇 위인가 — `outside` 는 훑은 수 안에 없다 · 셀 수 없거나 아직이면 `null` */
export type BrandHubRank = { kind: 'ranked'; rank: number } | { kind: 'outside' } | null;

/**
 * 순위 훑기의 줄 → 이 브랜드의 차례.
 * @param rows 훑기 답의 줄(순위 차례) — 아직이면 `null`
 * @param flagged 순위에서 뺄 브랜드(`toFlaggedBrands`) — 아직이면 `null`
 * @param scanSize 훑은 수 — 줄이 이만큼 찼는데 없으면 `outside`, 덜 찼는데 없으면 그 조건의 언급이 없다(`null`)
 * @returns 이 브랜드가 뺄 브랜드면 `null`(머리 밑 알림 줄이 그 까닭을 적는다)
 */
export function toBrandHubRank(
  name: string,
  rows: readonly { brand: string }[] | null | undefined,
  flagged: ReadonlyMap<string, BrandQualityFlag> | null | undefined,
  scanSize: number = BRAND_HUB_RANK_SCAN,
): BrandHubRank {
  const key = name.trim().toLowerCase();
  if (!key || !rows || !flagged) return null;
  if (flagged.has(key)) return null;
  const { kept } = splitFlaggedRanking(rows, (row) => row.brand, flagged);
  const index = kept.findIndex((row) => row.brand.trim().toLowerCase() === key);
  if (index >= 0) return { kind: 'ranked', rank: index + 1 };
  return rows.length >= scanSize ? { kind: 'outside' } : null;
}

/**
 * 이름 옆 표의 말 — 「12위」 · 「200위 밖」.
 * @returns 셀 수 없으면 빈 글자(표를 안 그린다)
 */
export function brandHubRankLabel(rank: BrandHubRank, scanSize: number = BRAND_HUB_RANK_SCAN): string {
  if (!rank) return '';
  return rank.kind === 'ranked' ? `${fNumber(rank.rank)}위` : `${fNumber(scanSize)}위 밖`;
}

/**
 * 훑은 줄 전부의 이름(순위 차례) — 머리 「N위」가 판정을 묻는 목록이다.
 * 🔑 판정 열쇠는 이 목록 그대로다 — 막대(`toBrandHubPeerQualityNames` 의 `scan`)도 이 함수를 써야 같은 열쇠로 한 번만 읽는다.
 */
export function toBrandHubRankScanNames(rows: readonly { brand: string }[] | null | undefined): string[] {
  return (rows ?? []).map((row) => row.brand);
}

/**
 * 순위를 매기려면 판정이 있어야 할 브랜드 — 훑기의 첫 줄부터 이 브랜드까지(공유 페이지가 판정 문서를 읽는 범위).
 * @returns 이 브랜드가 훑기에 없으면 빈 배열(위 줄의 판정과 무관하게 `outside` 다)
 */
export function toBrandHubRankQualityNames(name: string, rows: readonly { brand: string }[] | null | undefined): string[] {
  const key = name.trim().toLowerCase();
  const list = rows ?? [];
  const index = list.findIndex((row) => row.brand.trim().toLowerCase() === key);
  return index >= 0 ? list.slice(0, index + 1).map((row) => row.brand) : [];
}
